import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  doSearch,
  fetchFilters,
  resetCurrentFilters,
  setAppliedFilters,
  setModelTypeAppliedFilter,
  setSearchModelType,
  setSearchPeriod,
  setSelectedPersonFilter,
} from "../../../../store/reducer/_searchReducer";
import { hideFilterModal } from "../../../../store/reducer/_uiReducer";
import {
  getCurrentSearchFilters,
  getCurrentSearchPeriod,
  getFilterModal,
  getModelTypeSearchFilters,
  getQueryValue,
  getSearchFiltersLoading,
  getSearchModelType,
  getSearchResultsType,
  getSearchType,
  getSelectedPersonFilter,
} from "../../../../store/selectors";
import { countCheckedFilters } from "../../../../utils";
import { PDLButton, PDLTypography } from "../../dna";
import { ContentFilters } from "../../global";
import LexLoading from "../../global/_lexLoading";
import ModelTypeFilterSection from "../../global/_modelTypeFilterSection";
import "./index.scss";

const ModalFilers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const showModal = useSelector(getFilterModal);
  const searchType = useSelector(getSearchType);
  const searchResultsType = useSelector(getSearchResultsType);
  const currentFilters = useSelector(getCurrentSearchFilters);
  const filtersLoading = useSelector(getSearchFiltersLoading);
  const queryValue = useSelector(getQueryValue);
  const selectedPersonFilter = useSelector(getSelectedPersonFilter);

  const appliedModelType = useSelector(getSearchModelType);
  const searchModelTypeFilters = useSelector(getModelTypeSearchFilters);

  const [modelType, setModelType] = useState(appliedModelType);
  const filters = useSelector(getModelTypeSearchFilters);
  const currentModelTypeFilters = filters?.[modelType] || [];

  const currentPeriod = useSelector(getCurrentSearchPeriod);

  const [period, setPeriod] = useState(currentPeriod);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [personFilterValue, setPersonFilterValue] = useState("");

  const handleClose = useCallback(() => {
    dispatch(hideFilterModal());
  }, [dispatch]);

  const doFetchFilters = useCallback(() => {
    dispatch(fetchFilters({ searchType }));
  }, [dispatch, searchType]);

  const resetFiltersLoaded = () => {
    if (filtersLoaded && !filtersLoading && !currentFilters.length) {
      setFiltersLoaded(false);
    }
  };

  const resetFilters = useCallback(() => {
    dispatch(resetCurrentFilters());
  }, [dispatch]);

  const hasAnySelected = useCallback((options = [], arr = []) => {
    options.forEach((curOption) => {
      if (curOption.type === "option" && curOption.checked) {
        arr.push(curOption.label);
      } else {
        hasAnySelected(curOption.options, arr);
      }
    });
  }, []);

  const getSelectedFilters = useCallback(() => {
    const appliedFilters = [];
    currentFilters.forEach((filter) => {
      const appliedForCurrent = [];
      hasAnySelected(filter.options, appliedForCurrent);
      if (appliedForCurrent.length) {
        appliedFilters.push({ id: filter.id, values: appliedForCurrent });
      }
    });
    return appliedFilters;
  }, [currentFilters, hasAnySelected]);

  const extractCheckedValues = (options = [], result = []) => {
    options.forEach((option) => {
      if (option.type === "option" && option.checked) {
        result.push(option.label);
      }

      if (option.options?.length) {
        extractCheckedValues(option.options, result);
      }
    });
  };

  const getSelectedModelTypeFilters = useCallback(() => {
    const result = [];

    const current = searchModelTypeFilters?.[modelType];
    if (!current) return [];

    current.forEach((filter) => {
      extractCheckedValues(filter.options, result);
    });

    return result;
  }, [searchModelTypeFilters, modelType]);

  const countSelected = useMemo(() => {
    const filterCount = currentFilters.reduce(
      (acc, filter) => acc + countCheckedFilters(filter),
      0
    );

    const modelTypeFilters = currentModelTypeFilters.reduce(
      (acc, filter) => acc + countCheckedFilters(filter),
      0
    );

    const modelTypeCount =
      searchType?.id === "modelos"
        ? modelTypeFilters + (modelType !== "all" ? 1 : 0)
        : 0;

    const periodCount = period?.period && period.period !== "always" ? 1 : 0;

    return (
      filterCount +
      periodCount +
      (personFilterValue?.length > 2 ? 1 : 0) +
      modelTypeCount
    );
  }, [
    currentFilters,
    period?.period,
    personFilterValue,
    currentModelTypeFilters,
  ]);

  const applyFilters = useCallback(() => {
    dispatch(setAppliedFilters({ appliedFilters: getSelectedFilters() }));
    dispatch(hideFilterModal());
    dispatch(setSearchModelType(modelType));

    dispatch(
      setModelTypeAppliedFilter({
        modelType,
        filters: getSelectedModelTypeFilters(),
      })
    );
    dispatch(
      setSelectedPersonFilter({
        searchType: searchType.id,
        value: personFilterValue?.length > 2 ? personFilterValue : "",
      })
    );
    dispatch(
      setSearchPeriod({
        searchType: searchType.id,
        period: period.period,
        startDate: period.startDate,
        endDate: period.endDate,
      })
    );

    dispatch(doSearch({ query: queryValue, searchType }));
    if (searchResultsType === searchType.id) {
      navigate("/resultados");
    }
  }, [
    dispatch,
    getSelectedFilters,
    navigate,
    queryValue,
    searchResultsType,
    searchType,
    period?.period,
    period?.startDate,
    period?.endDate,
    personFilterValue,
    modelType,
    getSelectedModelTypeFilters,
  ]);

  useEffect(() => {
    resetFiltersLoaded();
  }, [searchType]);

  useEffect(() => {
    if (
      showModal &&
      !currentFilters?.length &&
      !filtersLoading &&
      !filtersLoaded
    ) {
      doFetchFilters();
      setFiltersLoaded(true);
    }
  }, [
    currentFilters,
    doFetchFilters,
    filtersLoading,
    filtersLoaded,
    showModal,
  ]);

  useEffect(() => {
    setPeriod(currentPeriod);
  }, [currentPeriod]);

  useEffect(() => {
    setModelType(appliedModelType);
  }, [appliedModelType]);

  useEffect(() => {
    if (showModal) {
      setPersonFilterValue(selectedPersonFilter || "");
    }
  }, [showModal, selectedPersonFilter]);

  return (
    <Modal
      backdrop="static"
      contentClassName="h-100 d-flex flex-column align-items-stretch justify-content-between gap-4"
      dialogClassName="h-100"
      show={showModal}
      onHide={handleClose}
      className="pdl_component_modal_filtros d-flex flex-row align-items-stretch justify-content-end"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <PDLTypography display size="xl" variant="semibold">
            Filtros
          </PDLTypography>
          <PDLTypography size="sm" variant="regular">
            Aplique filtros a sua pesquisa {searchType.id}
          </PDLTypography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {filtersLoading ? (
          <div className="loading-content">
            <LexLoading size={100} dur={1} customText="Buscando filtros..." />
          </div>
        ) : currentFilters ? (
          <ContentFilters
            filters={currentFilters}
            onPeriodChange={setPeriod}
            period={period}
            personFilterValue={personFilterValue}
            setPersonFilterValue={setPersonFilterValue}
          />
        ) : (
          "Sem filtros disponíveis para a pesquisa selecionada."
        )}

        {!filtersLoading && searchType?.id === "modelos" && (
          <ModelTypeFilterSection
            modelType={modelType}
            setModelType={setModelType}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-row align-items-center justify-content-between px-4">
        <PDLButton
          variant="link"
          size="md"
          onClick={() => {
            resetFilters();
            setModelType("all");
            setPeriod({
              period: "always",
              startDate: null,
              endDate: null,
            });
            setPersonFilterValue("");
          }}
        >
          Limpar
        </PDLButton>
        <PDLButton
          variant="secondary"
          size="md"
          onClick={handleClose}
          className="gray"
        >
          Cancelar
        </PDLButton>
        <PDLButton variant="primary" size="md" onClick={applyFilters}>
          Aplicar ({countSelected})
        </PDLButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalFilers;
